import paginationFactory from "react-bootstrap-table2-paginator";
import ToolkitProvider, { Search } from "react-bootstrap-table2-toolkit/dist/react-bootstrap-table2-toolkit";
import BootstrapTable from "react-bootstrap-table-next";
import React, { useEffect, useState, useContext } from "react";
import { Table, Spinner, Alert, Container, Card, Button, Modal, Form } from "react-bootstrap";
import { InputGroup, InputGroupText, Input, FormGroup } from "reactstrap";

import { useNavigate, useLocation, Link } from "react-router-dom";
import { ThemeContext } from "contexts/ThemeContext.jsx";
import { resolveSingularName } from "./configs/Naming";
import { serverHost } from "variables/serverdata";
import { hasPermission, PERMISSIONS } from "utils/permissions";
import { sortRows } from "./util/helpers";
import HusariaHeader from "../components/HusariaHeader";

const GenericTable = ({
  tableName,
  title,
  singularTitle,
  parentName = "HUCS",
  addRoute,
  editBaseRoute,
  visibleColumns,
  rowKey,
  booleanColumns = [],
  groupBy,
  sections = [],
  useBootstrapTable = true,
}) => {
  const { theme } = useContext(ThemeContext);
  const isDark = theme === "dark";
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deleteModal, setDeleteModal] = useState(false);
  const [rowToDelete, setRowToDelete] = useState(null);
  const [search, setSearch] = useState("");
  const navigate = useNavigate();
  const location = useLocation();
  const currentPath = window.location.pathname;
  const advanced = [
    "domains", "domain settings", "default settings",
    "variables", "sip profiles", "sofia global settings",
    "modules", "access controls", "email templates",
    "databases", "number translations"
  ]
  const userData = React.useMemo(() => {
    const storedUser = localStorage.getItem("user");
    return storedUser ? JSON.parse(storedUser) : null;
  }, []);
  const isAdvanced = advanced.includes(title.toLowerCase())

  const canEdit = hasPermission(userData, PERMISSIONS.EDIT);

  const canPerformActions =
    hasPermission(userData, PERMISSIONS.CREATE) ||
    hasPermission(userData, PERMISSIONS.EDIT) ||
    hasPermission(userData, PERMISSIONS.DELETE);

  const [openSections, setOpenSections] = React.useState(() => {
    const initial = sections.reduce((acc, s) => {
      acc[s.label] = false;
      return acc;
    }, {});

    // Open section if passed from navigation state
    if (location.state?.openSection) {
      initial[location.state.openSection] = true;
    }

    return initial;
  });

  const columns = visibleColumns.map((col, index) => ({
    dataField: col.key,
    text: col.label,
    sort: true,
    formatter: (cell, row) => {
      if (booleanColumns.includes(col.key)) {
        return formatBoolean(cell);
      }

      if (index === 0) {
        return canEdit ? (
          <a
            onClick={() =>
              navigate(
                typeof editBaseRoute === "function"
                  ? editBaseRoute(row)
                  : `${editBaseRoute}/${tableName}/${row[rowKey]}`
              )
            }
            style={{
              color: isDark ? "#60a5fa" : "#0d6efd",
              fontWeight: 600,
              cursor: "pointer",
              textDecoration: "none",
            }}
          >
            {cell ?? "-"}
          </a>
        ) : (
          <span style={{ fontWeight: 600 }}>{cell ?? "-"}</span>
        );
      }

      return cell ?? "-";
    },
  }));

  if (canPerformActions) {
    columns.push({
      dataField: "actions",
      text: "Actions",
      formatter: (_, row) =>
        hasPermission(userData, PERMISSIONS.DELETE) ? (
          <Button
            variant="danger"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              toggleDeleteModal(row);
            }}
          >
            Delete
          </Button>
        ) : null,
    });
  }

  const pagination = paginationFactory({
    page: 1,
    alwaysShowAllBtns: true,
    showTotal: true,
    withFirstAndLast: false,
    sizePerPageRenderer: ({ onSizePerPageChange }) => (
      <div className="dataTables_length">
        <label>
          Show{" "}
          <select
            className="form-control form-control-sm"
            onChange={(e) => onSizePerPageChange(e.target.value)}
          >
            <option value="10">10</option>
            <option value="25">25</option>
            <option value="50">50</option>
            <option value="100">100</option>
          </select>{" "}
          entries.
        </label>
      </div>
    ),
  });

  const toggleSection = (groupName) => {
    setOpenSections((prev) => ({
      ...prev,
      [groupName]: !prev[groupName],
    }));
  };

  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: "asc", // "asc" | "desc"
  });

  const handleSort = (key) => {
    setSortConfig((prev) => {
      if (prev.key === key) {
        return {
          key,
          direction: prev.direction === "asc" ? "desc" : "asc",
        };
      }
      return { key, direction: "asc" };
    });
  };

  const filterRows = (rows) => {
    if (!search) return rows;

    return rows.filter((row) =>
      visibleColumns.some((col) =>
        String(row[col.key] ?? "")
          .toLowerCase()
          .includes(search.toLowerCase())
      )
    );
  };

  const filteredRows = React.useMemo(() => {
    if (!search) return rows;

    return rows.filter((row) =>
      visibleColumns.some((col) =>
        String(row[col.key] ?? "")
          .toLowerCase()
          .includes(search.toLowerCase())
      )
    );
  }, [rows, search, visibleColumns]);

  const fetchRows = async () => {
    setLoading(true);
    try {

      let routeType = "";
      if (currentPath.includes("inbound-routes")) routeType = "inbound";
      else if (currentPath.includes("outbound-routes")) routeType = "outbound";

      const currentSite = JSON.parse(localStorage.getItem("selectedSite"));
      const site_uuid = currentSite?.site_uuid || null;

      const response = await fetch(
        `${serverHost}/get_rows?table=${tableName}&pageSize=1000&page=1&routeType=${routeType}&domain_uuid=${site_uuid}`
      );

      if (!response.ok) throw new Error(`Failed to fetch ${tableName}`);

      const data = await response.json();
      setRows(data.results || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    fetchRows();
  }, [tableName]);

  const toggleDeleteModal = (row = null) => {
    setRowToDelete(row);
    setDeleteModal(!deleteModal);
  };

  const handleDelete = async () => {
    if (!rowToDelete) return;
    const site = JSON.parse(localStorage.getItem("selectedSite"));

    try {
      let response;
      let url = `${serverHost}/delete`;
      if (currentPath.includes("domain")) url = `${serverHost}/delete_domain`;
      {
        // Call the generic delete endpoint
        response = await fetch(url, {
          method: "DELETE",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            table: tableName,
            where: { [rowKey]: rowToDelete[rowKey] },
            client_uuid: site.client_uuid,
            module_name: "HUCS",
            module_uuid: "35525bfe-5ee5-4ef7-8bd6-560d489803d5",
          }),
        });
      }

      if (!response.ok) throw new Error("Delete failed");

      await response.json();
      toggleDeleteModal();
      fetchRows();
    } catch (err) {
      alert(`Error deleting row: ${err.message}`);
    }
  };

  const formatBoolean = (val) => (val === true || val === "t" || val === "true" ? "Yes" : "No");

  const resolvedSingular = resolveSingularName(singularTitle, tableName);
  const editRoute = resolvedSingular === "Destination" ? `${editBaseRoute}/destination/` : `${editBaseRoute}/${tableName}/`;
  const rowEvents = {
    onClick: (e, row) => {
      navigate(
        typeof editBaseRoute === "function"
          ? editBaseRoute(row)
          : `${editRoute}${row[rowKey]}`
      );
    },
  };
  if (loading)
    return (
      <div className="text-center mt-5">
        <Spinner animation="border" variant={isDark ? "light" : "primary"} />
        <p className="mt-2" style={{ color: isDark ? "#e2e8f0" : "#212529" }}>
          Loading {title?.toLowerCase() || "data"}...
        </p>
      </div>
    );

  if (error)
    return (
      <Alert variant="danger" className="mt-5 text-center">
        {error}
      </Alert>
    );

  // Map sections for fast lookup
  const sectionMap = sections.reduce((acc, s) => {
    acc[s.value] = s.label;
    return acc;
  }, {});

  // Group rows only if groupBy + sections exist
  let groupedRows;
  if (groupBy && sections.length > 0) {
    // Map sections for fast lookup
    const sectionMap = sections.reduce((acc, s) => {
      acc[s.value] = s.label;
      return acc;
    }, {});

    // Initialize grouped object with all sections + "Other"
    const grouped = {};
    sections.forEach((s) => (grouped[s.label] = []));
    grouped["Other"] = [];

    filteredRows.forEach((row) => {

      const sectionValue = row[groupBy];
      const groupName = sectionMap[sectionValue] || "Other";
      grouped[groupName].push(row);
    });

    // Remove empty sections
    groupedRows = Object.fromEntries(
      Object.entries(grouped).filter(([_, arr]) => arr.length > 0)
    );
  } else {
    // No grouping, just render all rows normally
    groupedRows = { "": rows }; // single "empty" group
  }

  return (
    <>
      <HusariaHeader name={title} parentName={parentName} padding="4.5rem" />

      <Container className="mt--6" fluid>
         {isAdvanced && (<div style={{marginBottom: "8px"}}>
                <span className="return-dealer-menu" onClick={() => navigate('/admin/dealer-menu')}>
                  <i class="fas fa-caret-left"></i> Return to Dealer Menu
                </span>
                </div>)}
        <Card className={`shadow-sm ${isDark ? "bg-dark text-light border-dark" : ""}`}>
          
          <Card.Header
            className="d-flex justify-content-between align-items-center"
            style={{
              backgroundColor: isDark ? "#1f2937" : "#f0f0f0",
              color: isDark ? "#e2e8f0" : "#111",
            }}
          >
            
            {/* LEFT SIDE: Title + Search */}
            <div className="d-flex align-items-center">

              <Form className="navbar-search navbar-search-light form-inline">
                <FormGroup className="mb-0">
                  <InputGroup className="input-group-alternative input-group-merge search-border">
                    <InputGroupText>
                      <i className="fas fa-search search-icon" style={{ color: isDark ? "#b0b0b0" : "rgb(102, 102, 102)" }} />
                    </InputGroupText>
                    <Input
                      className="form-control table-search-bar"
                      placeholder="Search"
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                    />
                  </InputGroup>
                </FormGroup>
              </Form>
            </div>

            {/* RIGHT SIDE: Actions */}
            <div className="d-flex gap-2">
              {tableName === "v_sip_profiles" && (
                <Button
                  variant="light"
                  size="sm"
                  onClick={() =>
                    navigate(`/admin/hucs/sofiaGlobalSettings?setting_uuid=${rowKey}`)
                  }
                >
                  Settings
                </Button>
              )}

              <Button variant="info" size="sm" onClick={fetchRows}>
                Refresh
              </Button>

              {hasPermission(userData, PERMISSIONS.CREATE) && (
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => navigate(addRoute)}
                >
                  + Add {resolvedSingular}
                </Button>
              )}
            </div>
          </Card.Header>

          <Card.Body style={{ padding: 0, backgroundColor: isDark ? "#1f2937" : "#fff" }}>
            <div style={{ width: "100%", overflowX: "hidden" }}>
              {sections.length > 0 && groupBy ? (
                <>
                  {Object.entries(groupedRows).map(([groupName, rowsInGroup]) => {
                    const isOpen = openSections[groupName];

                    return (
                      <div key={groupName} style={{ marginBottom: "1rem" }}>
                        {/* ===== Section Header ===== */}
                        <div
                          onClick={() => toggleSection(groupName)}
                          style={{
                            cursor: "pointer",
                            backgroundColor: isDark ? "#111827" : "#e2e8f0",
                            color: isDark ? "#e5e7eb" : "#1f2937",
                            padding: "0.75rem 1rem",
                            fontWeight: 700,
                            display: "flex",
                            alignItems: "center",
                            gap: "0.5rem",
                            borderRadius: "4px",
                          }}
                        >
                          <span>{isOpen ? "▼" : "▶"}</span> {groupName}
                        </div>

                        {/* ===== Expanded Section Table ===== */}
                        {isOpen && (
                          <div
                            className="section-table-wrapper"
                            style={{
                              overflowX: "auto",
                              backgroundColor: isDark ? "#1f2937" : "#fff",
                              border: isDark ? "1px solid #374151" : "1px solid #d1d5db",
                              borderTop: "none",
                              borderRadius: "0 0 4px 4px",
                              padding: 0,
                            }}
                          >
                            <Table
                              bordered
                              className="mb-0"
                              style={{
                                margin: 0,
                                tableLayout: "fixed",
                                width: "100%",
                                backgroundColor: isDark ? "#1f2937" : "#fff",
                              }}
                            >
                              <thead>
                                <tr>
                                  {visibleColumns.map((col) => (
                                    <th
                                      key={col.key}
                                      className="text-center"
                                      onClick={() => handleSort(col.key)}
                                      style={{
                                        cursor: "pointer",
                                        userSelect: "none",
                                        whiteSpace: "nowrap",
                                      }}
                                    >
                                      {col.label}
                                      {sortConfig.key === col.key && (
                                        <span style={{ marginLeft: "6px" }}>
                                          {sortConfig.direction === "asc" ? "▲" : "▼"}
                                        </span>
                                      )}
                                    </th>

                                  ))}

                                  {canPerformActions && (
                                    <th className="text-center" style={{ width: "100px" }}>
                                      Actions
                                    </th>
                                  )}
                                </tr>
                              </thead>

                              <tbody>
                                {sortRows(filterRows(rowsInGroup), sortConfig).map((row) => {
                                  const idValue = row[rowKey];
                                  return (
                                    <tr
                                      role="button"
                                      key={idValue}
                                      onClick={() => {
                                        console.log("Clicked!")
                                        if (canEdit) {
                                          navigate(
                                            typeof editBaseRoute === "function"
                                              ? editBaseRoute(row)
                                              : `${editBaseRoute}/${tableName}/${idValue}`
                                          );
                                        }
                                      }}
                                    >
                                      {visibleColumns.map((col, index) => {
                                        const value = row[col.key] ?? "-";
                                        const isLink = index === 0;
                                        return (
                                          <td
                                            key={`${idValue}-${col.key}`}
                                            className="text-center"
                                            style={{
                                              padding: "0.5rem",
                                              overflow: "hidden",
                                              textOverflow: "ellipsis",
                                              whiteSpace: "nowrap",
                                            }}
                                            title={value}
                                          >
                                            {isLink ? (
                                              canEdit ? (
                                                <span
                                                  style={{
                                                    color: isDark ? "#60a5fa" : "#0d6efd",
                                                    fontWeight: 600,
                                                  }}
                                                >
                                                  {value}
                                                </span>
                                              ) : (
                                                <span style={{ fontWeight: 600 }}>{value}</span>
                                              )
                                            ) : booleanColumns.includes(col.key) ? (
                                              formatBoolean(value)
                                            ) : (
                                              <span style={{ fontWeight: 400 }}>{value}</span>
                                            )}
                                          </td>
                                        );
                                      })}
                                      {canPerformActions && (
                                        <td
                                          className="text-center"
                                          style={{ width: "100px" }}
                                          onClick={(e) => e.stopPropagation()} // Prevent row click when clicking button
                                        >
                                          {hasPermission(userData, PERMISSIONS.DELETE) && (
                                            <Button
                                              variant="danger"
                                              size="sm"
                                              onClick={() => toggleDeleteModal(row)}
                                            >
                                              Delete
                                            </Button>
                                          )}
                                        </td>
                                      )}
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </Table>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </>
              ) : (
                /* ===================== NON-SECTIONED TABLE ===================== */
                <div className="py-4 table-responsive">
                  <BootstrapTable
                    keyField={rowKey}
                    data={filteredRows}  // <-- use filteredRows instead of all rows
                    columns={columns}
                    bootstrap4
                    pagination={pagination}
                    bordered={false}
                    hover
                    rowEvents={rowEvents}
                    rowStyle={{ cursor: "pointer" }}
                    classes="align-items-center table-flush"
                    noDataIndication={() =>
                      loading ? (
                        <div className="text-center">Loading...</div>
                      ) : (
                        <div className="text-center">No records found.</div>
                      )
                    }
                  />
                </div>
              )}
            </div>
          </Card.Body>
        </Card>
        {/* Need to check if this table is under variable subsection => add return nav button*/ }
      </Container>

      {/* ===================== DELETE MODAL ===================== */}
      <Modal show={deleteModal} onHide={toggleDeleteModal}>
        <Modal.Header
          closeButton
          style={{
            backgroundColor: isDark ? "#1f2937" : "#fff",
          }}
        >
          <Modal.Title style={{ color: isDark ? "#e2e8f0" : "#212529" }}>
            Delete {resolvedSingular}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body
          style={{
            backgroundColor: isDark ? "#1f2937" : "#fff",
            color: isDark ? "#e2e8f0" : "#212529",
          }}
        >
          Are you sure you want to delete this {resolvedSingular}? 
          <br />
          <strong>This action cannot be undone.</strong>
        </Modal.Body>
        <Modal.Footer
          style={{
            backgroundColor: isDark ? "#111827" : "#f8f9fa",
          }}
        >
          <Button variant="light" onClick={toggleDeleteModal}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDelete}>
            Delete
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}

export default GenericTable;
